(() => {
  if (window.FXBGArtworkLoader) return;
  let pdfModule;
  let workerSource;
  const error = (message) => new Error(message);

  async function imageCanvas(blob) {
    const url = URL.createObjectURL(blob);
    try {
      const image = new Image();
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(error('Could not open this image. Export a PNG, JPG, or PDF and try again.'));
        image.src = url;
      });
      if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 40000000) {
        throw error('This image is too large to preview. Export a smaller copy.');
      }
      const scale = Math.min(1, 1400 / Math.max(image.naturalWidth, image.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
      return canvas;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  function sanitizeSvg(text) {
    if (/<!DOCTYPE|<!ENTITY/i.test(text)) throw error('SVG document declarations are not supported. Export a plain SVG or PNG.');
    const xml = new DOMParser().parseFromString(text, 'image/svg+xml');
    const root = xml.documentElement;
    if (xml.querySelector('parsererror') || root.localName !== 'svg') throw error('This SVG could not be read. Export a plain SVG or PNG.');
    const allowed = new Set(['svg','g','path','rect','circle','ellipse','polygon','polyline','line','text','tspan','defs','linearGradient','radialGradient','stop','clipPath','mask','use','title','desc','pattern','image','style']);
    const localCss = (css) => !/@import|@font-face|expression\s*\(|javascript:/i.test(css) &&
      [...css.matchAll(/url\s*\(([^)]*)\)/gi)].every(match => /^#[\w:.-]+$/.test(match[1].trim().replace(/^['"]|['"]$/g,'')));
    const clean = (node) => {
      for (const child of [...node.children]) {
        if (!allowed.has(child.localName) || child.namespaceURI !== 'http://www.w3.org/2000/svg') { child.remove(); continue; }
        if (child.localName === 'style' && !localCss(child.textContent)) {
          throw error('This SVG uses external styles or fonts. Embed them or export text as outlines.');
        }
        clean(child);
      }
      for (const attribute of [...node.attributes]) {
        const name = attribute.localName, value = attribute.value.trim();
        if (/^on/i.test(name)) { node.removeAttributeNode(attribute); continue; }
        if (name === 'href' && !/^#[\w:.-]+$/.test(value) && !/^data:image\/(png|jpeg|webp);base64,[a-z0-9+/=\s]+$/i.test(value)) {
          throw error('This SVG links to an external image. Embed the image or export a PNG.');
        }
        if ((name === 'style' || /url\s*\(/i.test(value)) && !localCss(value)) {
          throw error('This SVG uses external resources. Embed them or export a PNG.');
        }
      }
    };
    clean(root);
    const box = (root.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
    const dimension = (name) => {
      const value = root.getAttribute(name) || '';
      return /^\d+(\.\d+)?(px)?$/.test(value) ? parseFloat(value) : 0;
    };
    const w = box.length === 4 ? box[2] : dimension('width');
    const h = box.length === 4 ? box[3] : dimension('height');
    if (!(w > 0 && h > 0 && Number.isFinite(w/h))) throw error('SVG needs valid dimensions or a viewBox. Export a plain SVG or PNG.');
    const scale = Math.min(1, 1400 / Math.max(w,h));
    root.setAttribute('xmlns','http://www.w3.org/2000/svg');
    root.setAttribute('width',String(Math.max(1,Math.round(w*scale))));
    root.setAttribute('height',String(Math.max(1,Math.round(h*scale))));
    if (box.length !== 4) root.setAttribute('viewBox',`0 0 ${w} ${h}`);
    return new Blob([new XMLSerializer().serializeToString(root)], {type:'image/svg+xml'});
  }

  async function open(file, config) {
    if (file.size > 50 * 1024 * 1024) throw error('Preview supports files up to 50 MB. Export a smaller copy.');
    const signature = new TextDecoder('latin1').decode(await file.slice(0,1024).arrayBuffer());
    const ext = file.name.split('.').pop().toLowerCase();
    const isPdf = /%PDF-\d/.test(signature);
    if (ext === 'eps' || (!isPdf && ext === 'ai')) {
      throw error('This AI/EPS file needs conversion for preview. Export a PDF-compatible AI, PDF, SVG, or PNG. Your original file stays selected for production review.');
    }
    if (isPdf || ext === 'pdf') {
      const parts=[config.pdfWorkerPart1Url,config.pdfWorkerPart2Url,config.pdfWorkerPart3Url].filter(Boolean);
      if (!config.pdfModuleUrl || (!config.pdfWorkerUrl && parts.length!==3)) throw error('PDF preview assets are missing. Please contact the shop or upload a PNG.');
      const moduleUrl = new URL(config.pdfModuleUrl,location.href).href;
      pdfModule ||= import(moduleUrl).catch(() => {
        pdfModule = null;
        throw error('PDF preview could not load. Refresh the page or upload a PNG export.');
      });
      const pdf = await pdfModule;
      let workerUrl;
      if(parts.length===3){
        workerSource ||= Promise.all(parts.map(async url=>{
          const response=await fetch(new URL(url,location.href));
          if(!response.ok)throw error('PDF preview could not load. Refresh the page or upload a PNG export.');
          return response.text();
        })).then(parts=>URL.createObjectURL(new Blob([parts.join('')],{type:'application/javascript'}))).catch(e=>{workerSource=null;throw e;});
        workerUrl=await workerSource;
      }else workerUrl = new URL(config.pdfWorkerUrl,location.href).href;
      pdf.GlobalWorkerOptions.workerSrc = workerUrl;
      const task = pdf.getDocument({data:new Uint8Array(await file.arrayBuffer()),isEvalSupported:false,useWasm:false,useSystemFonts:true});
      task.onPassword = () => { task.destroy().catch(()=>{}); };
      let doc;
      try { doc = await task.promise; }
      catch (e) {
        task.destroy().catch(()=>{});
        if (/password|destroyed/i.test(e.message || '')) throw error('Password-protected PDFs need an unlocked copy for preview.');
        throw error('Could not open this PDF. Export a fresh PDF or PNG and try again.');
      }
      let renderTask, destroyed = false;
      return {
        kind: ext === 'ai' ? 'AI (PDF-compatible)' : 'PDF',
        pages: doc.numPages,
        async renderPage(number) {
          renderTask?.cancel();
          const page = await doc.getPage(number);
          if (destroyed) throw error('Preview cancelled.');
          const base = page.getViewport({scale:1});
          const scale = Math.min(3,1400/Math.max(base.width,base.height));
          const viewport = page.getViewport({scale});
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1,Math.ceil(viewport.width));
          canvas.height = Math.max(1,Math.ceil(viewport.height));
          renderTask = page.render({canvasContext:canvas.getContext('2d'),viewport,background:'rgba(0,0,0,0)'});
          await renderTask.promise;
          return canvas;
        },
        destroy() {
          destroyed = true;
          renderTask?.cancel();
          task.destroy().catch(()=>{});
        }
      };
    }
    let blob = file;
    if (ext === 'svg' || file.type === 'image/svg+xml') blob = sanitizeSvg(await file.text());
    else if (!['png','jpg','jpeg','webp','gif','bmp'].includes(ext)) {
      throw error('Preview supports PDF, SVG, PNG, JPG, WebP, GIF, BMP, and PDF-compatible AI. Keep this file for production and use a supported export for preview.');
    }
    const canvas = await imageCanvas(blob);
    return {kind:ext.toUpperCase(),pages:1,renderPage:async()=>canvas,destroy(){}};
  }
  window.FXBGArtworkLoader = {open,sanitizeSvg};
})();
